import React, { useContext } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, registerables, ChartOptions, ChartData } from 'chart.js';
import font from '../../config/Fonts';
import { formatDate } from '../../helpers/moment';
import { LanguageContext } from '../../contexts/useLanguage';
import useMedia from '../../hooks/useMedia';
import { COLOR_DAILY_VISITS } from '../../constants';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { formatNumber } from '../../helpers/FormatCurrency';
import { DailyGrossRevenue } from '../../models/grossRevenue/DailyGrossRevenue';
ChartJS.register(...registerables);

type Props = {
    dailyGrossRevenueStatistic: DailyGrossRevenue[];
};

export default function BarChartGrossRevenue(props: Props) {
    const { isMobileSM, isMediumMD } = useMedia();
    const { t } = useContext(LanguageContext);

    const data: ChartData<'bar', number[], string> = {
        labels:
            props.dailyGrossRevenueStatistic &&
            props.dailyGrossRevenueStatistic.map((item) => formatDate.getLocalDay2(item.inDate)),
        datasets: [
            {
                label: t('dashboard:text.payByWeb'),
                data:
                    props.dailyGrossRevenueStatistic &&
                    props.dailyGrossRevenueStatistic.map((item) => item.qtyTotalOfPayByWeb!),
                backgroundColor: COLOR_DAILY_VISITS.other,
            },
            {
                label: t('dashboard:text.payByPhone'),
                data:
                    props.dailyGrossRevenueStatistic &&
                    props.dailyGrossRevenueStatistic.map((item) => item.qtyTotalOfPayByPhone!),
                backgroundColor: COLOR_DAILY_VISITS.ev,
            },
            {
                label: t('dashboard:text.webKiosk'),
                data:
                    props.dailyGrossRevenueStatistic &&
                    props.dailyGrossRevenueStatistic.map((item) => item.qtyTotalOfWebKiosk!),
                backgroundColor: COLOR_DAILY_VISITS.petrol,
            },
            {
                label: t('dashboard:text.kiosk'),
                data:
                    props.dailyGrossRevenueStatistic &&
                    props.dailyGrossRevenueStatistic.map((item) => item.qtyTotalOfKiosk!),
                backgroundColor: COLOR_DAILY_VISITS.diesel,
            },
        ],
    };

    const options: ChartOptions<'bar'> = {
        responsive: true,
        // set height not crash
        maintainAspectRatio: false,
        plugins: {
            //note
            legend: {
                display: false,
                position: 'bottom' as const,
                align: 'center',
                //set note label font
                labels: {
                    font: {
                        family: font,
                        size: isMobileSM ? 12 : isMediumMD ? 14 : 16,
                    },
                    padding: isMobileSM ? 10 : 20,
                    boxWidth: 5,
                    boxHeight: 5,
                    usePointStyle: true,
                    pointStyle: 'circle',
                },
            },

            // Top Label
            datalabels: {
                display: true,
                color: 'black',
                anchor: 'end',
                align: 'top',
                font: {
                    family: font,
                    size: isMobileSM || props.dailyGrossRevenueStatistic.length > 10 ? 9 : 11,
                },

                formatter(value, context) {
                    const dataSetArr: number[] = [];
                    context.chart.data.datasets.forEach((item) => {
                        if (item.data[context.dataIndex] !== undefined) {
                            dataSetArr.push(Number(item.data[context.dataIndex]));
                        }
                    });
                    let total = 0;
                    if (dataSetArr.length > 0) {
                        total = dataSetArr.reduce((total, item) => (total += item), 0);
                    }
                    if (context.datasetIndex === dataSetArr.length - 1) {
                        return '£' + formatNumber.formatEURO(total);
                    }
                    return '';
                },
            },

            tooltip: {
                enabled: true,
                callbacks: {
                    label(tooltipItems: any) {
                        return ` ${tooltipItems.dataset.label}: £${tooltipItems.formattedValue}`;
                    },
                },
            },
        },
        layout: {
            padding: {
                top: 20,
            },
        },
        //setting grid
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        family: font,
                    },
                },
                stacked: true,
            },
            y: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        family: font,
                    },
                    //y step
                    stepSize: 1000,
                    callback(this, tickValue, index, ticks) {
                        return '£' + formatNumber.formatEURO(Number(tickValue));
                    },
                },
                //y label start at 0
                beginAtZero: true,
                min: 0,
                stacked: true,
            },
        },
    };

    return <Bar options={options} data={data} plugins={[ChartDataLabels]} />;
}
